import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logsService, Log } from "../services/logs.service";
import { useSelectedColoc } from "../hooks/useSelectedColoc";
import { formatDateTime } from "../utils/format";
import "../styles/LogsPage.css";
import { getErrorMessage } from '../utils/error';

const PendingInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const limit = 10;

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await logsService.getLogs(page, limit, "error");
      setLogs(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalLogs(response.meta.total);
      setLoading(false);
    } catch (err) {
      setError("Impossible de charger les factures en attente");
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, selectedColoc]);

  useEffect(() => {
    setPage(1);
  }, [selectedColoc]);

  const handleLogClick = (logId: string) => {
    navigate(`/pending-invoices/error/${logId}`);
  };

  const handleDismiss = async (e: React.MouseEvent, logId: string) => {
    e.stopPropagation();
    if (!window.confirm("Voulez-vous vraiment rejeter cette facture ?")) return;

    setDismissingId(logId);
    try {
      await logsService.dismissLog(logId);
      fetchLogs();
    } catch (err) {
      alert(getErrorMessage(err, "Erreur lors du rejet"));
    } finally {
      setDismissingId(null);
    }
  };

  const handleRetry = async (e: React.MouseEvent, logId: string) => {
    e.stopPropagation();
    if (!window.confirm("Voulez-vous renvoyer cette facture dans le pipeline de traitement automatique ?")) return;

    setRetryingId(logId);
    try {
      await logsService.retryLog(logId);
      fetchLogs();
    } catch (err) {
      alert(getErrorMessage(err, "Erreur lors du retraitement"));
    } finally {
      setRetryingId(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === logs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(logs.map((l) => l.id)));
    }
  };

  const handleBulkRetry = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Renvoyer ${selectedIds.size} facture(s) au traitement automatique ?`)) return;

    setBulkLoading(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => logsService.retryLog(id))
      );
      setSelectedIds(new Set());
      fetchLogs();
    } catch (err) {
      alert("Erreur lors du retraitement groupé");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDismiss = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Rejeter ${selectedIds.size} facture(s) ?`)) return;

    setBulkLoading(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => logsService.dismissLog(id))
      );
      setSelectedIds(new Set());
      fetchLogs();
    } catch (err) {
      alert("Erreur lors du rejet groupé");
    } finally {
      setBulkLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-indicator">Chargement des factures en attente...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="logs-container">
      <div className="logs-header-section">
        <h2>Factures en attente</h2>
        {selectedColoc && (
          <div className="selected-coloc-info">
            Colocation: <span className="coloc-name">{selectedColoc.name}</span>
          </div>
        )}
      </div>
      <p>Factures que le systeme n'a pas pu traiter automatiquement et qui doivent etre qualifiees manuellement.</p>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-action-bar">
          <span className="bulk-count">{selectedIds.size} sélectionnée(s)</span>
          <button
            className="retry-button"
            onClick={handleBulkRetry}
            disabled={bulkLoading}
          >
            {bulkLoading ? "..." : "Retraiter"}
          </button>
          <button
            className="dismiss-button"
            onClick={handleBulkDismiss}
            disabled={bulkLoading}
          >
            {bulkLoading ? "..." : "Rejeter"}
          </button>
          <button
            className="bulk-cancel"
            onClick={() => setSelectedIds(new Set())}
          >
            Annuler
          </button>
        </div>
      )}

      <div className="logs-list">
        <div className="logs-header">
          <div className="log-column checkbox-column">
            <input
              type="checkbox"
              checked={logs.length > 0 && selectedIds.size === logs.length}
              onChange={toggleSelectAll}
            />
          </div>
          <div className="log-column date-column">Date</div>
          <div className="log-column file-column">Nom du fichier</div>
          <div className="log-column error-column">Message d'erreur</div>
          <div className="log-column actions-column">Actions</div>
        </div>

        {logs.length > 0 ? (
          logs.map(log => (
            <div
              key={log.id}
              className="log-row clickable"
              onClick={() => handleLogClick(log.id)}
            >
              <div className="log-column checkbox-column" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(log.id)}
                  onChange={() => toggleSelect(log.id)}
                />
              </div>
              <div className="log-column date-column">{formatDateTime(log.createdAt)}</div>
              <div className="log-column file-column">{log.fileName}</div>
              <div className="log-column error-column">{log.errorMessage}</div>
              <div className="log-column actions-column pending-actions">
                <button
                  className="retry-button"
                  onClick={(e) => handleRetry(e, log.id)}
                  disabled={retryingId === log.id || dismissingId === log.id}
                  title="Renvoyer au traitement automatique"
                >
                  {retryingId === log.id ? "..." : "Retraiter"}
                </button>
                <button
                  className="dismiss-button"
                  onClick={(e) => handleDismiss(e, log.id)}
                  disabled={dismissingId === log.id || retryingId === log.id}
                  title="Rejeter cette facture"
                >
                  {dismissingId === log.id ? "..." : "Rejeter"}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-logs-message">
            Aucune facture en attente de traitement.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Precedent
          </button>

          <span className="pagination-info">
            Page {page} sur {totalPages} ({totalLogs} factures)
          </span>

          <button
            className="pagination-button"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

export default PendingInvoicesPage;
